import { RemoteTable } from '@neo/blocks/data-table/data-table'
import type { DataTableFilterField } from '@neo/blocks/data-table/types'
import { StatusIndicator, toneFromBadgeVariant } from '@neo/primitives/status-indicator'
import { Tooltip } from '@neo/primitives/tooltip'
import { formatDuration } from '@shared/utils'
import type { ColumnDef } from '@tanstack/react-table'
import { humanize } from 'inflection'
import { CircleAlert } from 'lucide-react'
import { useMemo } from 'react'
import { useParams } from 'router-v6'
import { useFlowSection } from 'v2/pages/flow/flow-section'
import { FlowRunStatus, type RFlowRun } from 'v2/pages/flow/types'
import { CurrencyRenderer } from 'v2/shared/components/currency'
import { SubaccountCell } from 'v2/shared/components/subaccount-cell'
import {
  toSubaccountFilterOptions,
  toSubaccountNames,
  useAllSubaccountsQuery,
} from 'v2/shared/hooks/use-all-subaccounts'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { flowService } from 'v2/shared/services/flow-service'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { formatDateTime, getStatusVariant } from '../flow-runs/utils'

const DURATION_OPTIONS = [
  { label: 'Any', value: '-1' },
  { label: '0 sec', value: '0' },
  { label: '30 sec', value: '30' },
  { label: '1 min', value: '60' },
  { label: '5 min', value: '300' },
  { label: '15 min', value: '900' },
  { label: '30 min', value: '1800' },
  { label: '1 hr', value: '3600' },
]

export const AgentFlowRunsTab = () => {
  const { id = '' } = useParams()
  const navigate = useAppNavigate()
  const { builderBase, runsBase } = useFlowSection()

  // Subaccount UI is gated per-org. The empty fallbacks matter because the query
  // key is shared: another surface's cached list would otherwise still put the
  // column and filter on screen here.
  const { subaccountTaggingEnabled } = useFeatureFlags()
  // The filter's options exclude disabled subaccounts (a filter you
  // could newly pick is a NEW-selection source).
  const { data: subaccountsResult } = useAllSubaccountsQuery(subaccountTaggingEnabled)
  const subaccounts = subaccountsResult?.subaccounts
  const enabledSubaccountOptions = useMemo(
    () => (subaccountTaggingEnabled ? toSubaccountFilterOptions(subaccounts) : []),
    [subaccountTaggingEnabled, subaccounts],
  )
  // Worth a column even on a single agent's tab: a run keeps the tag it acted as,
  // so re-tagging the agent leaves older runs attributed to the old subaccount.
  const subaccountNames = useMemo(
    () => (subaccountTaggingEnabled ? toSubaccountNames(subaccounts) : new Map<string, string>()),
    [subaccountTaggingEnabled, subaccounts],
  )

  const columns = useMemo<ColumnDef<RFlowRun>[]>(
    () => [
      {
        accessorKey: 'phlo_run_uuid',
        header: "Run ID's",
        meta: {
          width: '320px',
          cellClass: 'max-w-none whitespace-nowrap font-jetbrains text-xs-400 tabular-nums',
        },
      },
      ...(subaccountNames.size
        ? [
            {
              accessorKey: 'sub_auth_id',
              header: 'Sub Account',
              cell: ({ row }) => (
                <SubaccountCell subAuthId={row.original.sub_auth_id} names={subaccountNames} />
              ),
            } satisfies ColumnDef<RFlowRun>,
          ]
        : []),
      {
        accessorKey: 'total_cost',
        meta: { cellClass: 'font-jetbrains text-xs-400 tabular-nums' },
        header: 'Cost',
        cell: ({ row }) => {
          const value = row.original.total_cost
          if (value == null) return null
          return <CurrencyRenderer value={value} />
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue, row }) => {
          const status = getValue() as FlowRunStatus
          const concurrentLimit = row.original.concurrent_limit_applied

          return (
            <div className="flex items-center gap-2">
              <StatusIndicator tone={toneFromBadgeVariant(getStatusVariant(status))}>
                {humanize(status)}
              </StatusIndicator>
              {concurrentLimit && (
                <Tooltip content="Call initiation failed because the maximum concurrent call limit has been reached">
                  <CircleAlert className="size-4 text-destructive" />
                </Tooltip>
              )}
            </div>
          )
        },
      },
      {
        accessorKey: 'duration',
        meta: { cellClass: 'font-jetbrains text-xs-400 tabular-nums' },
        header: 'Duration',
        cell: ({ getValue }) => {
          const value = getValue() as number
          return formatDuration(value)
        },
      },
      {
        accessorKey: 'started_at',
        meta: { cellClass: 'font-jetbrains text-xs-400 tabular-nums' },
        header: 'Start time',
        cell: ({ getValue }) => {
          const value = getValue() as number
          return formatDateTime(value)
        },
      },
    ],
    [subaccountNames],
  )

  const filterFields = useMemo<DataTableFilterField<RFlowRun>[]>(
    () => [
      {
        label: 'Run ID',
        value: 'phlo_run_uuid',
        paramKey: 'pattern',
        placeholder: 'Search',
      },
      // Only once there is something to pick (the options are empty for a flag-on
      // account with no subaccounts, and while the list is still loading) — same rule
      // as the global Agent Runs page and the Agents list.
      ...(subaccountTaggingEnabled && enabledSubaccountOptions.length
        ? [
            {
              // Mirrors the global Agent Runs page and the Agents-list filter: multi-select,
              // led by "Main Account" (untagged runs), disabled subaccounts excluded.
              label: 'Sub Account',
              value: 'sub_auth_id',
              paramKey: 'sub_auth_id',
              multiSelect: true,
              options: enabledSubaccountOptions,
              className: 'w-[350px]',
            } satisfies DataTableFilterField<RFlowRun>,
          ]
        : []),
      {
        label: 'Status',
        value: 'status',
        paramKey: 'status',
        multiSelect: false,
        options: [
          { label: 'Completed', value: FlowRunStatus.COMPLETED },
          { label: 'Failed', value: FlowRunStatus.FAILED },
          { label: 'In Progress', value: FlowRunStatus.IN_PROGRESS },
          { label: 'Queued', value: FlowRunStatus.QUEUED },
          { label: 'Expired', value: FlowRunStatus.EXPIRED },
        ],
      },
      {
        label: 'Start time',
        value: 'started_at',
        paramKey: 'started_at',
        isDateRange: true,
        dateRangeOptions: {
          disableFuture: true,
          minDate: new Date(new Date().setDate(new Date().getDate() - 30)),
          maxDate: new Date(),
          startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
          endDate: new Date(),
        },
      },
      {
        label: 'Duration',
        value: 'duration',
        paramKey: 'duration',
        isDurationRange: true,
        durationRangeOptions: {
          fromOptions: DURATION_OPTIONS,
          toOptions: DURATION_OPTIONS,
        },
      },
      {
        label: 'Test runs',
        value: 'is_playground',
        paramKey: 'is_playground',
        isBoolean: true,
      },
    ],
    [enabledSubaccountOptions, subaccountTaggingEnabled],
  )

  return (
    <div className="h-full flex flex-col">
      <RemoteTable
        staleTime={0}
        refetchInterval={10000}
        columns={columns}
        queryKey={[...flowQueryKeys().paginatedFlowRuns, id]}
        fetchService={(params) => flowService.listFlowRunsPaginated({ ...params, phlo_uuid: id })}
        defaultPageSize={50}
        filterFields={filterFields}
        extractData={(data) => ({
          data: (data as { runs: RFlowRun[]; total: number }).runs,
          totalCount: (data as { runs: RFlowRun[]; total: number }).total,
        })}
        onRowClick={(row) => {
          if (row.status === FlowRunStatus.QUEUED) return
          navigate(`${runsBase}/${row.phlo_uuid}/${row.phlo_run_uuid}`, {
            state: { from: `${builderBase}/${id}/runs` },
          })
        }}
      />
    </div>
  )
}
